import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  min2CharValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createAmenities,
  getAllAmenities,
  getAmenitiesByID,
  AmenitiesImageUpload,
  updateAmenities,
} from "./services/amenities.service";
import { clearAmenitiesDetails } from "./amenitiesSlice";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import { getFile } from "../../_helper/helper";
import { IProperty } from "../property/propertyModel";
import { getAllActiveProperties } from "../property/services/property.services";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";

const CreateAmenities: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  // const location = useLocation();
  // const AmenitiesID = location.state?.AmenitiesID;
  const AmenitiesID = useAppSelector((state) => state.drawer.data);
  const AmenitiesDetail = useAppSelector(
    (state) => state.amenities.AmenitiesDetail,
  );
  const loading = useAppSelector((state) => state.amenities.formLoading);
  const PropertyList = useAppSelector((state) => state.property.PropertyList);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [AmenitiesDetail]);

  useEffect(() => {
    dispatch(getAllActiveProperties());
    if (AmenitiesID) {
      dispatch(getAmenitiesByID(AmenitiesID));
    }
    return () => {
      dispatch(clearAmenitiesDetails());
    };
  }, [AmenitiesID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      AmenitiesName: values?.AmenitiesName ? values?.AmenitiesName : "",
      PropertyID: values?.PropertyID ? values?.PropertyID?.join(",") : "",
      IsActive: values?.IsActive === 1,
    };
    const ImageData = new FormData();
    const Image =
      values?.Image && typeof values?.Image !== "string"
        ? getFile(values?.Image)
        : "";

    try {
      if (AmenitiesID) {
        const editPayload = {
          ...formData,
          ID: AmenitiesID,
        };

        const response = await dispatch(updateAmenities(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());

          if (values?.Image && typeof values?.Image !== "string") {
            ImageData.append("Image", Image);
            ImageData.append("ID", AmenitiesID);
            await dispatch(AmenitiesImageUpload(ImageData));
          }
          dispatch(getAllAmenities());
          dispatch(clearAmenitiesDetails());
        }
      } else {
        const response = await dispatch(createAmenities(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", Image);

            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(AmenitiesImageUpload(ImageData));
          }
          dispatch(getAllAmenities());
          dispatch(clearAmenitiesDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={AmenitiesDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {AmenitiesID ? "Update Amenity" : "Add Amenity"}
                  </Typography.h4>

                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"PropertyID"}
                    name={"PropertyID"}
                    label={"Property Name"}
                    placeholder="Property Name"
                    component={FormMultiSelectionFiled}
                    validator={requiredValidator}
                    astrike={true}
                    options={PropertyList?.map((property: IProperty) => {
                      return {
                        value: property?.ID,
                        label: property?.PropertyName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="AmenitiesName"
                    label="Amenities Name"
                    placeholder="Amenities Name"
                    component={FormTextField}
                    validator={min2CharValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={AmenitiesID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateAmenities;
